import { useTranslation } from 'react-i18next';
import { Shield, FileText, Cookie, AlertCircle } from 'lucide-react';

const Policies = () => {
  const { i18n } = useTranslation();
  const el = i18n.language === 'el';

  return (
    <div className="bg-stone-50 min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-sans font-semibold mb-4" data-testid="policies-title">
            {el ? 'Πολιτικές & Νομικά' : 'Policies & Legal'}
          </h1>
          <p className="text-base md:text-lg text-stone-600 font-light">
            {el ? 'Σημαντικές πληροφορίες σχετικά με τη διαμονή σας και τους όρους μας' : 'Important information about your stay and our terms'}
          </p>
        </div>

        {/* Privacy Policy (GDPR) */}
        <section className="bg-white p-8 rounded-sm border border-stone-200 mb-8" data-testid="privacy-section">
          <div className="flex items-center mb-6">
            <Shield className="w-8 h-8 text-primary mr-3 shrink-0" />
            <h2 className="text-2xl font-sans font-semibold">
              {el ? 'Πολιτική Απορρήτου (Συμμόρφωση GDPR)' : 'Privacy Policy (GDPR Compliance)'}
            </h2>
          </div>
          <div className="space-y-4 text-stone-700 font-light leading-relaxed">
            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Συλλογή Δεδομένων' : 'Data Collection'}</h3>
            <p>
              {el
                ? 'Η InFox Suites συλλέγει και επεξεργάζεται προσωπικά δεδομένα σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (GDPR). Συλλέγουμε πληροφορίες που παρέχετε κατά την κράτηση, συμπεριλαμβανομένου του ονόματός σας, της διεύθυνσης email, του τηλεφώνου και των στοιχείων πληρωμής.'
                : 'InFox Suites collects and processes personal data in accordance with the General Data Protection Regulation (GDPR). We collect information you provide when making a booking, including your name, email address, phone number, and payment information.'}
            </p>
            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Πώς Χρησιμοποιούμε τα Δεδομένα σας' : 'How We Use Your Data'}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{el ? 'Για την επεξεργασία και επιβεβαίωση των κρατήσεών σας' : 'To process and confirm your bookings'}</li>
              <li>{el ? 'Για την αποστολή οδηγιών check-in και πληροφοριών καταλύματος' : 'To communicate check-in instructions and property information'}</li>
              <li>{el ? 'Για υποστήριξη πελατών και απάντηση σε ερωτήματα' : 'To provide customer support and respond to inquiries'}</li>
              <li>{el ? 'Για τη συμμόρφωση με νομικές υποχρεώσεις και φορολογικές απαιτήσεις' : 'To comply with legal obligations and tax requirements'}</li>
              <li>{el ? 'Για τη βελτίωση των υπηρεσιών και της εμπειρίας χρήστη' : 'To improve our services and user experience'}</li>
            </ul>
            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Αποθήκευση & Ασφάλεια Δεδομένων' : 'Data Storage & Security'}</h3>
            <p>
              {el
                ? 'Τα προσωπικά σας δεδομένα αποθηκεύονται με ασφάλεια και κρυπτογράφηση. Χρησιμοποιούμε μέτρα ασφαλείας βιομηχανικού επιπέδου για την προστασία των πληροφοριών σας.'
                : 'Your personal data is stored securely and encrypted. We use industry-standard security measures to protect your information from unauthorized access, disclosure, or misuse.'}
            </p>
            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Τα Δικαιώματά σας' : 'Your Rights'}</h3>
            <p>{el ? 'Σύμφωνα με τον GDPR, έχετε δικαίωμα:' : 'Under GDPR, you have the right to:'}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>{el ? 'Πρόσβαση στα προσωπικά σας δεδομένα' : 'Access your personal data'}</li>
              <li>{el ? 'Διόρθωση ανακριβών δεδομένων' : 'Request correction of inaccurate data'}</li>
              <li>{el ? 'Διαγραφή δεδομένων (δικαίωμα στη λήθη)' : 'Request deletion of your data (right to be forgotten)'}</li>
              <li>{el ? 'Εναντίωση στην επεξεργασία' : 'Object to processing of your data'}</li>
              <li>{el ? 'Φορητότητα δεδομένων' : 'Request data portability'}</li>
            </ul>
            <p className="text-sm text-stone-600">
              {el ? 'Για να ασκήσετε τα δικαιώματά σας, επικοινωνήστε μαζί μας στο' : 'To exercise these rights, contact us at'} infoxsuites@info-x.gr
            </p>
          </div>
        </section>

        {/* Cookie Policy */}
        <section className="bg-white p-8 rounded-sm border border-stone-200 mb-8" data-testid="cookie-section">
          <div className="flex items-center mb-6">
            <Cookie className="w-8 h-8 text-primary mr-3 shrink-0" />
            <h2 className="text-2xl font-sans font-semibold">{el ? 'Πολιτική Cookies' : 'Cookie Policy'}</h2>
          </div>
          <div className="space-y-4 text-stone-700 font-light leading-relaxed">
            <p>
              {el
                ? 'Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες παρακολούθησης για τη βελτίωση της εμπειρίας περιήγησης, την ανάλυση της επισκεψιμότητας και την κατανόηση των προτιμήσεων των χρηστών.'
                : 'We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand user preferences.'}
            </p>
            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Τύποι Cookies που Χρησιμοποιούμε' : 'Types of Cookies We Use'}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>{el ? 'Απαραίτητα Cookies:' : 'Essential Cookies:'}</strong> {el ? 'Απαιτούνται για τη σωστή λειτουργία της ιστοσελίδας' : 'Required for the website to function properly'}</li>
              <li><strong>{el ? 'Cookies Ανάλυσης:' : 'Analytics Cookies:'}</strong> {el ? 'Μας βοηθούν να κατανοήσουμε πώς χρησιμοποιούν οι επισκέπτες τον ιστότοπό μας' : 'Help us understand how visitors use our site'}</li>
              <li><strong>{el ? 'Cookies Προτιμήσεων:' : 'Preference Cookies:'}</strong> {el ? 'Αποθηκεύουν τη γλώσσα και τις ρυθμίσεις σας' : 'Remember your language and regional settings'}</li>
            </ul>
            <p>
              {el
                ? 'Χρησιμοποιώντας τον ιστότοπό μας, συμφωνείτε με τη χρήση cookies σύμφωνα με αυτήν την πολιτική. Μπορείτε να διαχειριστείτε τις προτιμήσεις cookies μέσω των ρυθμίσεων του προγράμματος περιήγησής σας.'
                : 'By using our website, you consent to our use of cookies in accordance with this policy. You can manage cookie preferences through your browser settings.'}
            </p>
          </div>
        </section>

        {/* Terms & Conditions */}
        <section className="bg-white p-8 rounded-sm border border-stone-200 mb-8" data-testid="terms-section">
          <div className="flex items-center mb-6">
            <FileText className="w-8 h-8 text-primary mr-3 shrink-0" />
            <h2 className="text-2xl font-sans font-semibold">{el ? 'Όροι & Προϋποθέσεις' : 'Terms & Conditions'}</h2>
          </div>
          <div className="space-y-4 text-stone-700 font-light leading-relaxed">
            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Κράτηση & Πληρωμή' : 'Booking & Payment'}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{el ? 'Όλες οι κρατήσεις υπόκεινται σε διαθεσιμότητα και επιβεβαίωση' : 'All bookings are subject to availability and confirmation'}</li>
              <li>{el ? 'Οι τιμές περιλαμβάνουν διαμονή, τέλη καθαρισμού και φόρο διαμονής' : 'Prices include accommodation, cleaning fees, and occupancy taxes'}</li>
              <li>{el ? 'Η πληρωμή μπορεί να γίνει εξ ολοκλήρου (100%) μέσω κάρτας ή ως προκαταβολή 35% μέσω τραπεζικής μεταφοράς' : 'Payment can be made in full (100%) by card, or as a 35% deposit via bank transfer'}</li>
              <li>{el ? 'Για προκαταβολή 35%, επικοινωνήστε μαζί μας μέσω τηλεφώνου, email ή WhatsApp' : 'For the 35% deposit option, contact us by phone, email, or WhatsApp'}</li>
              <li>{el ? 'Δεν υπάρχουν επιπλέον χρεώσεις επεξεργασίας κάρτας' : 'No additional card processing fees are charged'}</li>
            </ul>

            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Πολιτική Ακύρωσης' : 'Cancellation Policy'}</h3>
            <p>
              <strong>{el ? 'Δωρεάν ακύρωση:' : 'Free cancellation:'}</strong>{' '}
              {el
                ? 'Ακυρώστε περισσότερο από 21 ημέρες πριν την άφιξη για πλήρη επιστροφή χρημάτων.'
                : 'Cancel more than 21 days before check-in for a full refund.'}
            </p>
            <p>
              <strong>{el ? 'Μη επιστρεπτέα:' : 'Non-refundable:'}</strong>{' '}
              {el
                ? 'Εάν ακυρώσετε εντός 21 ημερών πριν την άφιξη, η κράτηση καθίσταται μη επιστρεπτέα. Δεν γίνεται επιστροφή χρημάτων.'
                : 'If you cancel within 21 days of check-in, the booking becomes non-refundable. No refund will be issued.'}
            </p>

            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Check-in & Check-out' : 'Check-in & Check-out'}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{el ? 'Ώρα check-in: από τις 15:00' : 'Check-in time: 15:00 onwards'}</li>
              <li>{el ? 'Ώρα check-out: 11:00' : 'Check-out time: 11:00'}</li>
              <li>{el ? 'Πρόωρο check-in ή καθυστερημένο check-out μπορεί να είναι διαθέσιμα κατόπιν αιτήματος (υπό διαθεσιμότητα)' : 'Early check-in or late check-out may be available upon request (subject to availability)'}</li>
              <li>{el ? 'Θα λάβετε οδηγίες check-in μέσω email πριν την άφιξή σας' : 'You will receive check-in instructions via email before arrival'}</li>
            </ul>

            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Ελάχιστη Διαμονή' : 'Minimum Stay Requirements'}</h3>
            <p>
              {el
                ? 'Κάθε κατάλυμα έχει απαίτηση ελάχιστης διαμονής που ποικίλλει ανά εποχή. Η ελάχιστη διαμονή εμφανίζεται σε κάθε σελίδα καταλύματος και εφαρμόζεται κατά την κράτηση.'
                : 'Each property has a minimum stay requirement that varies by season. Minimum stay is displayed on each property page and enforced during booking.'}
            </p>

            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Ασφάλιση' : 'Insurance'}</h3>
            <p>
              {el
                ? 'Όλα τα καταλύματα InFox Suites είναι πλήρως ασφαλισμένα. Συνιστούμε στους επισκέπτες να αγοράσουν ταξιδιωτική ασφάλεια για κάλυψη απρόβλεπτων περιστάσεων.'
                : 'All InFox Suites properties are fully insured. We recommend guests purchase travel insurance to cover unforeseen circumstances.'}
            </p>
          </div>
        </section>

        {/* House Rules */}
        <section className="bg-white p-8 rounded-sm border border-stone-200 mb-8" data-testid="house-rules-section">
          <div className="flex items-center mb-6">
            <AlertCircle className="w-8 h-8 text-primary mr-3 shrink-0" />
            <h2 className="text-2xl font-sans font-semibold">{el ? 'Κανόνες Διαμονής & Ευθύνη' : 'House Rules & Liability'}</h2>
          </div>
          <div className="space-y-4 text-stone-700 font-light leading-relaxed">
            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Γενικοί Κανόνες' : 'General Rules'}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>{el ? 'Απαγορεύεται το κάπνισμα' : 'No smoking'}</strong> {el ? 'εντός των καταλυμάτων' : 'inside the properties'}</li>
              <li><strong>{el ? 'Απαγορεύονται πάρτι ή εκδηλώσεις' : 'No parties or events'}</strong></li>
              <li><strong>{el ? 'Δεν επιτρέπονται κατοικίδια' : 'No pets'}</strong> {el ? '(εκτός αν αναφέρεται ρητά)' : '(unless specifically stated)'}</li>
              <li>{el ? 'Οι επισκέπτες πρέπει να σέβονται τους γείτονες και να ελαχιστοποιούν τον θόρυβο, ειδικά μετά τις 22:00' : 'Guests must respect neighbors and keep noise to a minimum, especially after 22:00'}</li>
              <li>{el ? 'Η μέγιστη χωρητικότητα δεν πρέπει να υπερβαίνεται' : 'Maximum occupancy must not be exceeded'}</li>
              <li>{el ? 'Οι επισκέπτες πρέπει να συμμορφώνονται με όλους τους τοπικούς νόμους' : 'Guests must comply with all local laws and regulations'}</li>
            </ul>

            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Ζημιές & Ευθύνη' : 'Damage & Liability'}</h3>
            <p>
              <strong>{el ? 'Ευθύνη Επισκέπτη:' : 'Guest Responsibility:'}</strong>{' '}
              {el
                ? 'Οι επισκέπτες είναι υπεύθυνοι για οποιεσδήποτε ζημιές ή φθορές κατά τη διάρκεια της διαμονής τους. Οι ζημιές πρέπει να αναφέρονται αμέσως.'
                : 'Guests are responsible for any damages or breakages that occur during their stay. Damages must be reported immediately upon discovery.'}
            </p>
            <p>
              <strong>{el ? 'Χαμένα Κλειδιά:' : 'Lost Keys:'}</strong>{' '}
              {el ? 'Χρέωση €15 για χαμένα κλειδιά ή κάρτες πρόσβασης.' : 'A fee of €15 applies for lost keys or access cards.'}
            </p>
            <p className="bg-amber-50 border border-amber-200 p-4 rounded-sm">
              <strong className="text-amber-900">{el ? 'Σημαντικό:' : 'Important:'}</strong>{' '}
              {el
                ? 'Η άρνηση πληρωμής για ζημιές μπορεί να οδηγήσει σε νομικές ενέργειες. Όλα τα καταλύματα είναι ασφαλισμένα, αλλά οι επισκέπτες παραμένουν υπεύθυνοι για ζημιές που προκαλούνται κατά τη διαμονή τους.'
                : 'Refusal to pay for damages may result in legal action. All properties are insured, but guests remain liable for damages caused during their stay.'}
            </p>

            <h3 className="font-semibold text-stone-900 text-lg">{el ? 'Ασφάλεια' : 'Safety & Security'}</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>{el ? 'Κλειδώνετε πόρτες και παράθυρα όταν φεύγετε' : 'Keep doors and windows locked when leaving the property'}</li>
              <li>{el ? 'Μην αφήνετε πολύτιμα αντικείμενα χωρίς επίβλεψη' : 'Do not leave valuables unattended'}</li>
              <li>{el ? 'Εξοικειωθείτε με τις εξόδους κινδύνου και τον εξοπλισμό πυρασφάλειας' : 'Familiarize yourself with emergency exits and fire safety equipment'}</li>
              <li>{el ? 'Αναφέρετε αμέσως οποιαδήποτε ανησυχία ασφαλείας' : 'Report any safety concerns immediately'}</li>
            </ul>
          </div>
        </section>

        {/* Invoicing */}
        <section className="bg-white p-8 rounded-sm border border-stone-200 mb-8" data-testid="invoicing-section">
          <div className="flex items-center mb-6">
            <FileText className="w-8 h-8 text-primary mr-3 shrink-0" />
            <h2 className="text-2xl font-sans font-semibold">
              {el ? 'Τιμολόγηση & Φορολογική Συμμόρφωση (ΑΑΔΕ)' : 'Invoicing & Tax Compliance (AADE)'}
            </h2>
          </div>
          <div className="space-y-4 text-stone-700 font-light leading-relaxed">
            <p>
              {el
                ? 'Η InFox Suites χρησιμοποιεί το Oxygen για ηλεκτρονική τιμολόγηση. Μετά από κάθε κράτηση, τιμολόγιο ή απόδειξη δημιουργείται αυτόματα και διαβιβάζεται στο σύστημα myDATA της ΑΑΔΕ (Ανεξάρτητη Αρχή Δημοσίων Εσόδων).'
                : 'InFox Suites uses Oxygen for electronic invoicing. After each booking, an invoice or receipt is automatically generated and transmitted to the Greek tax authority\'s myDATA system (AADE - Independent Authority for Public Revenue).'}
            </p>
            <p>
              {el
                ? 'Όλες οι τιμές που εμφανίζονται περιλαμβάνουν τους ισχύοντες φόρους, συμπεριλαμβανομένου του φόρου διαμονής. Δεν θα υπάρξουν επιπλέον χρεώσεις. Θα λάβετε το τιμολόγιό σας μέσω email μετά την επιβεβαίωση κράτησης.'
                : 'All prices displayed include applicable taxes, including accommodation tax. No additional charges will be applied. You will receive your invoice via email after booking confirmation.'}
            </p>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="bg-primary text-white p-8 rounded-sm text-center" data-testid="policies-contact">
          <h2 className="text-2xl font-sans font-semibold mb-4">
            {el ? 'Ερωτήσεις σχετικά με τις Πολιτικές μας;' : 'Questions About Our Policies?'}
          </h2>
          <p className="mb-6 text-white/90 font-light">
            {el
              ? 'Εάν έχετε ερωτήσεις σχετικά με τις πολιτικές ή τους όρους μας, μη διστάσετε να επικοινωνήσετε μαζί μας.'
              : 'If you have any questions about our policies or terms, please don\'t hesitate to contact us.'}
          </p>
          <div className="space-y-2">
            <p><strong>Email:</strong> <a href="mailto:infoxsuites@info-x.gr" className="underline">infoxsuites@info-x.gr</a></p>
            <p><strong>{el ? 'Τηλέφωνο' : 'Phone'}:</strong> <a href="tel:+306984154695" className="underline">+30 698 415 4695</a></p>
            <p><strong>WhatsApp:</strong> <a href="https://wa.me/306984154695" className="underline">+30 698 415 4695</a></p>
          </div>
        </section>

        <div className="text-center mt-8 text-sm text-stone-500">
          <p>{el ? 'Τελευταία ενημέρωση: Φεβρουάριος 2026' : 'Last updated: February 2026'}</p>
        </div>
      </div>
    </div>
  );
};

export default Policies;
